import React from 'react';

const MetricCard = ({ label, value, sub, subColor = 'text-gray-400', icon, iconBg }) => (
  <div className="stat-card rounded-2xl p-5 flex flex-col gap-2 hover:scale-[1.02] transition-transform duration-200">
    <div className="flex items-center justify-between">
      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{label}</p>
      <div className={`w-8 h-8 rounded-xl ${iconBg} flex items-center justify-center text-sm`}>
        <i className={`fas ${icon}`}></i>
      </div>
    </div>
    <p className="text-2xl font-bold text-gray-800 tabular-nums">{value}</p>
    <p className={`text-xs font-medium ${subColor}`}>{sub}</p>
  </div>
);

const LatencyBar = ({ label, ms, pct, color }) => (
  <div>
    <div className="flex items-center justify-between text-xs text-gray-600 mb-1.5">
      <span>{label}</span>
      <span className="font-semibold tabular-nums">{ms} ms</span>
    </div>
    <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
      <div
        className={`h-full ${color} rounded-full transition-all duration-700`}
        style={{ width: `${pct}%` }}
      />
    </div>
  </div>
);

export default function PerformancePanel({ stats }) {
  const handleExport = () => {
    const report = {
      product: 'SAP Business One — Agentic AI',
      generated_at: new Date().toISOString(),
      session: {
        queries: stats?.queriesToday ?? 0,
        avg_latency_ms: stats?.avgLatencyMs ?? null,
        rag_enabled: true,
        avg_inference_seconds: 1.18,
        tokens_per_sec: 68.4,
        rag_retrieval_overhead_ms: 240,
        docs_ingested: 312,
        tools_invoked: ['analyze_overdue_invoices', 'evaluate_stock_risk', 'validate_sales_order']
      },
      modules: ['collections', 'inventory', 'sales_credit'],
      note: 'Demo session report — all figures are simulated.'
    };
    const blob = new Blob([JSON.stringify(report, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'sap-b1-agent-session-report.json';
    document.body.appendChild(a); a.click();
    document.body.removeChild(a); URL.revokeObjectURL(url);
  };

  const latencyRows = [
    { label: 'Retrieval (RAG)',       ms: 240, pct: 20, color: 'bg-blue-300' },
    { label: 'Reasoning / planning',  ms: 410, pct: 35, color: 'bg-blue-500' },
    { label: 'Tool execution (ERP)',  ms: 290, pct: 24, color: 'bg-blue-400' },
    { label: 'Generation',           ms: 240, pct: 21, color: 'bg-blue-600' },
  ];

  const liveSessions = stats?.queriesToday ?? 0;
  const liveLatency  = stats?.avgLatencyMs != null ? `${(stats.avgLatencyMs / 1000).toFixed(2)}s` : '1.18s';

  return (
    <div className="flex-1 overflow-y-auto p-4 md:p-6">

      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-2xl bg-indigo-600 flex items-center justify-center text-white shadow-md">
          <i className="fas fa-gauge-high"></i>
        </div>
        <div>
          <h2 className="text-lg font-bold text-gray-800">Performance &amp; Telemetry</h2>
          <p className="text-xs text-gray-400">Inference latency · throughput · RAG overhead</p>
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mb-6">
        <MetricCard label="Avg inference time"     value={liveLatency} sub="within SLA"      subColor="text-emerald-500" icon="fa-stopwatch"     iconBg="bg-emerald-100 text-emerald-600" />
        <MetricCard label="Tokens / sec"           value="68.4"         sub="streaming"       subColor="text-emerald-500" icon="fa-bolt"          iconBg="bg-blue-100 text-blue-600" />
        <MetricCard label="RAG retrieval overhead" value="240 ms"       sub="312 docs indexed" subColor="text-blue-500"   icon="fa-book-open"    iconBg="bg-indigo-100 text-indigo-600" />
        <MetricCard label="Queries this session"   value={liveSessions} sub="live counter"   subColor="text-emerald-500" icon="fa-chart-simple" iconBg="bg-violet-100 text-violet-600" />
      </div>

      {/* Latency breakdown + Export */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
        <div className="stat-card rounded-2xl p-5">
          <h3 className="text-sm font-semibold text-gray-700 mb-1">Latency Breakdown</h3>
          <p className="text-xs text-gray-400 mb-5">Where time is spent per request (avg 1.18s total)</p>
          <div className="space-y-4">
            {latencyRows.map(r => <LatencyBar key={r.label} {...r} />)}
          </div>
          <div className="mt-5 pt-4 border-t border-gray-100 flex items-center justify-between text-xs text-gray-400">
            <span>Total avg</span>
            <span className="font-bold text-gray-700 tabular-nums">1,180 ms</span>
          </div>
        </div>

        <div className="stat-card rounded-2xl p-5 border border-indigo-500/20 flex flex-col">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 rounded-xl bg-indigo-600 flex items-center justify-center text-white text-xs">
              <i className="fas fa-file-arrow-down"></i>
            </div>
            <h3 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>Session Report</h3>
            <span className="ml-auto text-[10px] bg-indigo-600 text-white px-2 py-0.5 rounded-full font-medium">JSON</span>
          </div>
          <p className="text-xs mb-4 flex-1" style={{ color: 'var(--text-muted)' }}>
            Export a machine-readable snapshot of this demo session: query count, RAG status, latency and tool usage — useful for sharing benchmark results with the client team.
          </p>

          {/* Live session summary */}
          <div className="grid grid-cols-2 gap-2 mb-4">
            {[
              { label: 'Queries',     value: liveSessions },
              { label: 'Avg latency', value: liveLatency },
              { label: 'RAG docs',    value: '312' },
              { label: 'Tools used',  value: '3' },
            ].map(({ label, value }) => (
              <div key={label} className="stat-card rounded-xl p-2.5 text-center">
                <p className="text-sm font-bold text-indigo-500 tabular-nums">{value}</p>
                <p className="text-[10px] uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>{label}</p>
              </div>
            ))}
          </div>

          <button
            onClick={handleExport}
            className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-indigo-600 to-indigo-700 text-white px-5 py-2.5 rounded-xl text-sm font-medium shadow hover:shadow-lg hover:scale-105 active:scale-100 transition-all duration-200"
          >
            <i className="fas fa-download text-xs"></i> Export JSON report
          </button>
        </div>
      </div>
    </div>
  );
}
